import { useId, useRef, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { ArrowUpRight, Check, ChevronDown, Upload } from 'lucide-react';

/**
 * Primitivas del sistema "lumin". Todo control es propio (nada con aspecto del
 * sistema operativo). Área táctil mínima 44 px (D31).
 */

// ── Botones ──────────────────────────────────────────────────────────────────
export function Boton({
  children,
  variante = 'primario',
  tamano = 'md',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: 'primario' | 'acento' | 'secundario' | 'contorno' | 'texto' | 'peligro';
  tamano?: 'sm' | 'md';
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-full font-medium transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45';
  const tam = tamano === 'sm' ? 'min-h-9 px-4 text-[13px]' : 'min-h-11 px-5 text-sm';
  const estilos = {
    primario: 'hover:opacity-90',
    acento: 'hover:brightness-95',
    secundario: 'bg-superficie text-principal shadow-[var(--sombra)] hover:bg-superficie2',
    contorno: 'border-[1.5px] border-[color-mix(in_srgb,var(--texto-suave)_35%,transparent)] text-principal hover:bg-superficie',
    texto: 'text-suave hover:text-principal px-2',
    peligro: 'text-rose-500 hover:bg-rose-500/10 px-3',
  } as const;
  const inline =
    variante === 'primario'
      ? { backgroundColor: 'var(--boton-bg)', color: 'var(--boton-texto)' }
      : variante === 'acento'
        ? { backgroundColor: 'var(--color-acento-var)', color: 'var(--color-acento-texto)' }
        : undefined;
  return (
    <button {...props} className={`${base} ${tam} ${estilos[variante]} ${className}`} style={inline}>
      {children}
    </button>
  );
}

/** Botón circular de icono (referencia: campana, ajustes, ↗ de tarjetas). */
export function BotonCirculo({
  children,
  contorno = false,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { contorno?: boolean }) {
  return (
    <button type="button" {...props} className={`circulo ${contorno ? 'circulo-contorno' : ''} ${className}`}>
      {children}
    </button>
  );
}

// ── Campos ───────────────────────────────────────────────────────────────────
function Etiqueta({ texto, htmlFor }: { texto: string; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-[13px] text-suave">
      {texto}
    </label>
  );
}

export function Campo({
  etiqueta,
  error,
  className = '',
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { etiqueta?: string; error?: string }) {
  const auto = useId();
  const ident = id ?? auto;
  return (
    <div className="block">
      {etiqueta ? <Etiqueta texto={etiqueta} htmlFor={ident} /> : null}
      <input id={ident} {...props} className={`campo ${className}`} />
      {error ? <span className="mt-1 block text-xs text-rose-500">{error}</span> : null}
    </div>
  );
}

export function Area({
  etiqueta,
  className = '',
  id,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { etiqueta?: string }) {
  const auto = useId();
  const ident = id ?? auto;
  return (
    <div className="block">
      {etiqueta ? <Etiqueta texto={etiqueta} htmlFor={ident} /> : null}
      <textarea id={ident} {...props} className={`campo ${className}`} />
    </div>
  );
}

/** Select propio: nativo por accesibilidad, pero con apariencia y chevron propios. */
export function Selector({
  etiqueta,
  children,
  className = '',
  id,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { etiqueta?: string }) {
  const auto = useId();
  const ident = id ?? auto;
  return (
    <div className="block">
      {etiqueta ? <Etiqueta texto={etiqueta} htmlFor={ident} /> : null}
      <div className="relative">
        <select id={ident} {...props} className={`campo ${className}`}>
          {children}
        </select>
        <ChevronDown
          size={16}
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-suave"
        />
      </div>
    </div>
  );
}

/** Casilla propia (no el checkbox del sistema). */
export function Casilla({
  activa,
  onCambiar,
  etiqueta,
  className = '',
}: {
  activa: boolean;
  onCambiar: (v: boolean) => void;
  etiqueta?: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={activa}
      onClick={() => onCambiar(!activa)}
      className={`inline-flex min-h-9 items-center gap-2.5 text-sm text-principal ${className}`}
    >
      <span className="casilla" data-activa={activa}>
        <Check size={13} strokeWidth={3} />
      </span>
      {etiqueta}
    </button>
  );
}

/** Interruptor (toggle) para booleanos. */
export function Interruptor({
  activo,
  onCambiar,
  etiqueta,
}: {
  activo: boolean;
  onCambiar: (v: boolean) => void;
  etiqueta?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      onClick={() => onCambiar(!activo)}
      className="inline-flex min-h-9 items-center gap-2.5 text-sm"
    >
      <span
        className="relative inline-block h-6 w-11 rounded-full transition"
        style={{ backgroundColor: activo ? 'var(--color-acento-var)' : 'var(--superficie-2)' }}
      >
        <span
          className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition"
          style={{ left: activo ? '1.375rem' : '0.125rem' }}
        />
      </span>
      {etiqueta}
    </button>
  );
}

/** Selector de archivo propio: el input nativo queda oculto. */
export function BotonArchivo({
  etiqueta,
  accept,
  onArchivo,
  nombre,
  variante = 'secundario',
}: {
  etiqueta: string;
  accept?: string;
  onArchivo: (archivo: File | null) => void;
  nombre?: string | null;
  variante?: 'secundario' | 'contorno' | 'primario';
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [local, setLocal] = useState<string | null>(null);
  const mostrado = nombre ?? local;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const a = e.target.files?.[0] ?? null;
          setLocal(a?.name ?? null);
          onArchivo(a);
        }}
      />
      <Boton type="button" variante={variante} onClick={() => ref.current?.click()}>
        <Upload size={15} /> {etiqueta}
      </Boton>
      {mostrado ? <span className="truncate text-xs text-suave">{mostrado}</span> : null}
    </div>
  );
}

// ── Superficies ──────────────────────────────────────────────────────────────
/** Tarjeta con título y acción circular ↗ opcional (como cada widget de la referencia). */
export function Tarjeta({
  titulo,
  subtitulo,
  accion,
  onAccion,
  tono = 'blanca',
  className = '',
  children,
}: {
  titulo?: ReactNode;
  subtitulo?: ReactNode;
  accion?: ReactNode;
  onAccion?: () => void;
  tono?: 'blanca' | 'negra' | 'acento';
  className?: string;
  children?: ReactNode;
}) {
  const clase = tono === 'negra' ? 'tarjeta-negra' : tono === 'acento' ? 'tarjeta-acento' : 'tarjeta';
  return (
    <section className={`${clase} relative p-5 ${className}`}>
      {titulo || accion || onAccion ? (
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            {titulo ? <h2 className="text-[17px] font-medium">{titulo}</h2> : null}
            {subtitulo ? <p className={`text-xs ${tono === 'blanca' ? 'text-suave' : 'opacity-60'}`}>{subtitulo}</p> : null}
          </div>
          {accion ?? (onAccion ? (
            <BotonCirculo contorno onClick={onAccion} aria-label="Abrir" className="h-10 w-10">
              <ArrowUpRight size={16} />
            </BotonCirculo>
          ) : null)}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/** Cabecera de página: ← opcional, título ligero grande, acciones a la derecha. */
export function EncabezadoPagina({
  titulo,
  subtitulo,
  atras,
  acciones,
}: {
  titulo: ReactNode;
  subtitulo?: ReactNode;
  atras?: () => void;
  acciones?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      {atras ? (
        <BotonCirculo contorno onClick={atras} aria-label="Volver">
          <ChevronDown size={18} className="rotate-90" />
        </BotonCirculo>
      ) : null}
      <div className="mr-auto">
        <h1 className="text-[28px] font-light leading-tight sm:text-[34px]">{titulo}</h1>
        {subtitulo ? <p className="text-sm text-suave">{subtitulo}</p> : null}
      </div>
      {acciones ? <div className="flex flex-wrap items-center gap-2">{acciones}</div> : null}
    </div>
  );
}

export function Avatar({ nombre, src, className = '' }: { nombre: string; src?: string | null; className?: string }) {
  return (
    <span className={`avatar ${className}`}>
      {src ? <img src={src} alt={nombre} /> : nombre.charAt(0).toUpperCase()}
    </span>
  );
}

export function Insignia({
  children,
  tono = 'gris',
}: {
  children: ReactNode;
  tono?: 'gris' | 'acento' | 'ok' | 'alerta' | 'negro';
}) {
  const estilos = {
    gris: 'bg-superficie2 text-suave',
    acento: '',
    ok: 'bg-emerald-500/15 text-emerald-600',
    alerta: 'bg-rose-500/15 text-rose-500',
    negro: 'bg-negro text-white',
  } as const;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ${estilos[tono]}`}
      style={tono === 'acento' ? { backgroundColor: 'var(--color-acento-var)', color: 'var(--color-acento-texto)' } : undefined}
    >
      {children}
    </span>
  );
}

export function Aviso({ tono = 'error', children }: { tono?: 'error' | 'ok'; children: ReactNode }) {
  const estilo = tono === 'error' ? 'bg-rose-500/10 text-rose-500' : 'bg-emerald-500/10 text-emerald-600';
  return (
    <p role="alert" className={`rounded-2xl px-4 py-3 text-sm ${estilo}`}>
      {children}
    </p>
  );
}

export function Cargando({ texto }: { texto: string }) {
  return <div className="flex min-h-40 items-center justify-center text-sm text-suave">{texto}</div>;
}

export function Chip({
  activo = false,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { activo?: boolean }) {
  return (
    <button type="button" {...props} className="chip" data-activo={activo}>
      {children}
    </button>
  );
}

/** Fila de lista con avatar + título + subtítulo + valor a la derecha (referencia: History). */
export function FilaLista({
  avatar,
  titulo,
  subtitulo,
  derecha,
  abajo,
  acciones,
}: {
  avatar: ReactNode;
  titulo: ReactNode;
  subtitulo?: ReactNode;
  derecha?: ReactNode;
  abajo?: ReactNode;
  acciones?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 py-3">
      {avatar}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-medium">{titulo}</p>
        {subtitulo ? <p className="truncate text-xs text-suave">{subtitulo}</p> : null}
      </div>
      {derecha ? <div className="text-right text-sm">{derecha}</div> : null}
      {abajo}
      {acciones ? <div className="flex flex-none items-center gap-1.5">{acciones}</div> : null}
    </div>
  );
}
